import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styles from './TalentApplicationDetailPage.module.css';
import Header from '../../../components/layout/Header/Header';
import TalentContentContainer from '../layout/TalentContentContainer';
import DetailHeader from './components/DetailHeader';
import DetailAnnouncement from './components/DetailAnnouncement';
import DetailSubmission from './components/DetailSubmission';
import DetailTimeline from './components/DetailTimeline';
import { getCastingDetail, getApplicationDetail } from '../../../api';
import { mapAppFromDetail, mapSubmissionFromDetail, mapAnnouncementFromCasting, buildTimeline } from './utils';
import { MOCK_APP, MOCK_ANNOUNCEMENT, MOCK_SUBMISSION, MOCK_TIMELINE } from './mockData';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

export default function TalentApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state: navState } = useLocation();
  const [searchParams] = useSearchParams();
  const isMock = searchParams.get('mock') === '1';

  const [appData, setAppData] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const [submission, setSubmission] = useState({ intro: '', files: [], videos: [] });
  const [timeline, setTimeline] = useState([]);
  const [isLoading, setIsLoading] = useState(!isMock);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (isMock) {
      setAppData(MOCK_APP);
      setAnnouncement(MOCK_ANNOUNCEMENT);
      setSubmission(MOCK_SUBMISSION);
      setTimeline(MOCK_TIMELINE);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      setIsError(false);
      try {
        const detail = await getApplicationDetail(id);
        setAppData(mapAppFromDetail(id, detail, navState));
        setSubmission(mapSubmissionFromDetail(detail));
        setTimeline(buildTimeline(detail));

        const castingPostId = detail?.castingPostId || navState?.castingPostId;
        if (castingPostId) {
          const casting = await getCastingDetail(castingPostId);
          setAnnouncement(mapAnnouncementFromCasting(casting));
        }
      } catch {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, isMock]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className={styles.errorWrapper}>
        <span className={styles.errorText}>데이터를 불러오지 못했습니다.</span>
        <button
          onClick={() => navigate('/talent/applications')}
          className={styles.errorButton}
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pageRoot}>
      <Header isSidebarPage={true} />
      <TalentContentContainer>
        {/* 뒤로 가기 */}
        <div className={styles.backButtonArea}>
          <button
            onClick={() => navigate('/talent/applications')}
            className={styles.backButton}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className={styles.backButtonLabel}>지원 현황으로 돌아가기</span>
          </button>
        </div>

        {/* 콘텐츠 래퍼 */}
        <div className={styles.contentWrapper}>
          {/* 헤더 카드 */}
          {appData && (
            <div className={styles.headerCard}>
              <DetailHeader app={appData} />
            </div>
          )}

          {/* 본문: 왼쪽 + 오른쪽 타임라인 */}
          <div className={styles.body}>
            <div className={styles.leftColumn}>
              {announcement && <DetailAnnouncement data={announcement} castingPostId={appData?.castingPostId} />}
              <DetailSubmission data={submission} />
            </div>
            <div className={styles.rightColumn}>
              <DetailTimeline items={timeline} />
            </div>
          </div>
        </div>
      </TalentContentContainer>
    </div>
  );
}
